import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const إدارةالحسابات = () => {
  const [activeTab, setActiveTab] = useState('المستخدمين');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [accountDetails, setAccountDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: 'male',
    birthDate: '1990-01-01'
  });
  const { showToast } = useToast();

  const tabs = [
    { id: 'المستخدمين', label: 'المستخدمين', userType: 'normalUser' },
    { id: 'أصحاب المتاجر', label: 'أصحاب المتاجر', userType: 'store' },
    { id: 'أصحاب المطابخ', label: 'أصحاب المطابخ', userType: 'restaurant' },
    { id: 'الادمن', label: 'الادمن', userType: 'admin' },
  ];

  const getCurrentUserType = () => {
    const currentTab = tabs.find(tab => tab.id === activeTab);
    return currentTab ? currentTab.userType : 'normalUser';
  };

  const fetchAccounts = async (updateAccountDetails = false) => {
    try {
      setLoading(true);
      const userType = getCurrentUserType();
      const response = await api.getAccountManagement(userType, page, 10);

      if (response && response.items) {
        const updatedAccounts = response.items;
        setAccounts(updatedAccounts);
        setTotalPages(response.totalPages || 1);

        // If updateAccountDetails is true and accountDetails modal is open, update it with fresh data
        if (updateAccountDetails) {
          // Use callback to get current accountDetails value
          setAccountDetails(prev => {
            if (prev && prev.id) {
              const updatedAccount = updatedAccounts.find(acc => acc.id === prev.id);
              if (updatedAccount) {
                // Get the most accurate status from the updated account - prioritize isActive from table
                // Explicitly check for boolean type to handle false correctly
                let finalIsActive;
                if (typeof updatedAccount.isActive === 'boolean') {
                  finalIsActive = updatedAccount.isActive;
                } else if (updatedAccount.status !== undefined) {
                  finalIsActive = updatedAccount.status === 'active';
                } else if (typeof updatedAccount.active === 'boolean') {
                  finalIsActive = updatedAccount.active;
                } else {
                  // Fallback to previous value if nothing is found
                  finalIsActive = prev.isActive !== undefined ? prev.isActive : false;
                }

                console.log('🔄 Updating accountDetails from fetchAccounts:', {
                  accountId: prev.id,
                  updatedAccountIsActive: updatedAccount.isActive,
                  updatedAccountIsActiveType: typeof updatedAccount.isActive,
                  updatedAccountStatus: updatedAccount.status,
                  updatedAccountActive: updatedAccount.active,
                  finalIsActive,
                  prevIsActive: prev.isActive,
                  updatedAccount: updatedAccount
                });

                const updated = {
                  ...prev,
                  ...updatedAccount,
                  // Preserve other fields from accountDetails
                  name: updatedAccount.name || prev.name || updatedAccount.fullName || prev.fullName,
                  fullName: updatedAccount.fullName || prev.fullName || updatedAccount.name || prev.name,
                  email: updatedAccount.email || prev.email,
                  city: updatedAccount.city || prev.city,
                  // Update status fields - use the most accurate status from table
                  isActive: finalIsActive,
                  status: finalIsActive ? 'active' : 'inactive',
                  active: finalIsActive
                };

                console.log('✅ Final accountDetails after fetchAccounts:', {
                  id: updated.id,
                  isActive: updated.isActive,
                  status: updated.status,
                  active: updated.active
                });

                return updated;
              }
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showToast('حدث خطأ في جلب البيانات', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [activeTab, page]);

  // Helper function to get account status consistently
  const getAccountStatus = (account) => {
    if (!account) {
      console.log('⚠️ getAccountStatus: account is null/undefined');
      return false;
    }
    // Priority: isActive (boolean) > status (string) > active (boolean)
    // Explicitly check for boolean values (including false)
    if (typeof account.isActive === 'boolean') {
      const result = account.isActive === true;
      console.log('✅ getAccountStatus from isActive:', {
        accountId: account.id,
        isActive: account.isActive,
        result
      });
      return result;
    }
    if (account.status !== undefined && account.status !== null) {
      const result = account.status === 'active';
      console.log('✅ getAccountStatus from status:', {
        accountId: account.id,
        status: account.status,
        result
      });
      return result;
    }
    if (typeof account.active === 'boolean') {
      const result = account.active === true;
      console.log('✅ getAccountStatus from active:', {
        accountId: account.id,
        active: account.active,
        result
      });
      return result;
    }
    console.log('⚠️ getAccountStatus: no valid status found, returning false', { account });
    return false;
  };

  const handleToggleStatus = async (id, newStatus) => {
    if (!id) {
      showToast('خطأ: لم يتم العثور على معرف الحساب', 'error');
      return;
    }

    try {
      const userType = getCurrentUserType();
      if (userType === 'normalUser' || userType === 'admin') {
        showToast('تغيير حالة المستخدم غير متاح حالياً', 'info');
        return;
      }

      const type = userType === 'restaurant' ? 'restaurant' : 'store';

      console.log('🔄 handleToggleStatus called:', { id, newStatus, type, newStatusType: typeof newStatus });

      // Update backend - ensure newStatus is explicitly boolean
      // newStatus should already be a boolean from !currentStatus
      // Convert to explicit boolean: true or false
      const isActiveValue = Boolean(newStatus);

      console.log('📤 Sending to backend:', {
        id,
        isActive: isActiveValue,
        isActiveType: typeof isActiveValue,
        newStatus,
        newStatusType: typeof newStatus,
        type
      });

      const result = await api.updateRestaurant(id, { isActive: isActiveValue }, type);

      console.log('📥 Backend response:', result);

      if (!result || (!result.id && result.success === false)) {
        throw new Error('فشل تحديث الحالة');
      }

      // Get actual status from backend response
      const actualIsActive = result.isActive !== undefined ? result.isActive : isActiveValue;

      console.log('✅ Final status:', { actualIsActive, resultIsActive: result.isActive });

      showToast(actualIsActive ? 'تم تفعيل المتجر بنجاح' : 'تم تعطيل المتجر بنجاح', 'success');

      // Update the account in the accounts array immediately
      setAccounts(prevAccounts =>
        prevAccounts.map(acc =>
          acc.id === id
            ? { ...acc, status: actualIsActive ? 'active' : 'inactive', active: actualIsActive, isActive: actualIsActive }
            : acc
        )
      );

      // Update accountDetails immediately with the new status - this is critical for UI update
      setAccountDetails(prev => {
        if (prev && prev.id === id) {
          const updated = {
            ...prev,
            isActive: actualIsActive,
            status: actualIsActive ? 'active' : 'inactive',
            active: actualIsActive
          };
          console.log('✅ Updated accountDetails immediately:', {
            id: updated.id,
            isActive: updated.isActive,
            status: updated.status,
            active: updated.active
          });
          return updated;
        }
        return prev;
      });

      // Re-fetch fresh data from backend to ensure consistency
      try {
        const freshDetails = await api.getRestaurantById(id, type);
        console.log('Fresh details after update:', freshDetails);

        if (freshDetails) {
          // Update accountDetails with fresh data from backend
          const freshIsActive = freshDetails.isActive !== undefined ? freshDetails.isActive : actualIsActive;
          setAccountDetails(prev => {
            if (prev && prev.id === id) {
              const updated = {
                ...prev,
                ...freshDetails,
                isActive: freshIsActive,
                status: freshIsActive ? 'active' : 'inactive',
                active: freshIsActive
              };
              console.log('✅ Updated accountDetails from fresh details:', {
                id: updated.id,
                isActive: updated.isActive,
                status: updated.status,
                active: updated.active
              });
              return updated;
            }
            return prev;
          });
        }
      } catch (fetchError) {
        console.error('Error fetching fresh details:', fetchError);
      }

      // Refresh accounts list to update table (this will overwrite with fresh data from backend)
      // Pass true to update accountDetails if modal is open
      await fetchAccounts(true);

      // Update selectedAccount if it's the same account
      setSelectedAccount(prev =>
        prev && prev.id === id
          ? { ...prev, status: actualIsActive ? 'active' : 'inactive', active: actualIsActive, isActive: actualIsActive }
          : prev
      );

    } catch (error) {
      console.error('Error toggling status:', error);
      showToast(error.message || 'حدث خطأ في تغيير الحالة', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) {
      return;
    }

    try {
      const userType = getCurrentUserType();
      if (userType === 'normalUser' || userType === 'admin') {
        await api.deleteUser(id);
      } else {
        // Determine type for restaurant/store
        const type = userType === 'restaurant' ? 'restaurant' : 'store';
        await api.deleteRestaurant(id, type);
      }
      showToast('تم حذف الحساب بنجاح', 'success');
      setShowDetailsModal(false);
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast(error.message || 'حدث خطأ في حذف الحساب', 'error');
    }
  };

  const handleEdit = (account, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingAccount(account);
    setShowEditModal(true);
  };

  const handleViewDetails = async (account) => {
    setSelectedAccount(account);
    setShowDetailsModal(true);
    setLoadingDetails(true);

    try {
      const userType = getCurrentUserType();
      let details = null;

      if (userType === 'normalUser' || userType === 'admin') {
        // Fetch user details
        details = await api.getUserById(account.id);
      } else {
        // Fetch restaurant/store details
        const type = userType === 'restaurant' ? 'restaurant' : 'store';
        details = await api.getRestaurantById(account.id, type);
      }

      console.log('Account details from API:', details);
      console.log('Selected account from table:', account);

      // Merge API data with account data from table
      // Priority: account from table (most recent) > details from API
      // For isActive: account.isActive from table takes priority (most recent after updates)
      const accountIsActive = getAccountStatus(account);
      const detailsIsActive = details?.isActive !== undefined ? details.isActive : (details?.status === 'active');
      // Use account status from table first (most recent), then fallback to API data
      const finalIsActive = accountIsActive !== undefined ? accountIsActive : detailsIsActive;

      const mergedData = {
        ...account,
        ...details,
        // Ensure we have name/fullName
        name: details?.name || account?.name || account?.fullName || '',
        fullName: details?.fullName || account?.fullName || details?.name || account?.name || '',
        // Ensure we have email
        email: details?.email || account?.email || details?.owner?.email || '',
        // Ensure we have city
        city: details?.city || account?.city || details?.country?.name || '',
        // Ensure we have status - prioritize account.isActive from table (most recent)
        isActive: finalIsActive,
        status: finalIsActive ? 'active' : 'inactive',
        // Keep owner info
        owner: details?.owner || account?.owner,
        // Keep other fields
        location: details?.location || account?.location,
        averageRating: details?.averageRating || account?.averageRating,
        ordersCount: details?.ordersCount || account?.ordersCount,
        profile_picture: details?.profile_picture || account?.profile_picture || details?.logo_url,
        logo_url: details?.logo_url || account?.logo_url,
      };

      console.log('📋 Merged account details:', mergedData);
      console.log('📊 Status check:', {
        detailsIsActive,
        accountIsActive,
        finalIsActive,
        detailsIsActiveValue: details?.isActive,
        detailsStatus: details?.status,
        accountStatus: account?.status,
        accountActive: account?.active
      });
      setAccountDetails(mergedData);
    } catch (error) {
      console.error('Error fetching account details:', error);
      // Use account data from table as fallback
      const fallbackIsActive = getAccountStatus(account);
      setAccountDetails({
        ...account,
        name: account?.name || account?.fullName || '',
        fullName: account?.fullName || account?.name || '',
        isActive: fallbackIsActive,
        status: fallbackIsActive ? 'active' : 'inactive',
      });
      showToast('تم تحميل البيانات من الجدول (حدث خطأ في جلب التفاصيل الكاملة)', 'warning');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingAccount) return;

    try {
      const userType = getCurrentUserType();
      if (userType === 'normalUser' || userType === 'admin') {
        const nameParts = (editingAccount.fullName || '').split(' ');
        await api.updateUser(editingAccount.id, {
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
        });
      } else {
        // Determine type for restaurant/store
        const type = userType === 'restaurant' ? 'restaurant' : 'store';
        await api.updateRestaurant(editingAccount.id, {
          name: editingAccount.name || editingAccount.fullName,
          isActive: editingAccount.status === 'active',
        }, type);
      }
      showToast('تم تحديث الحساب بنجاح', 'success');
      setShowEditModal(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Error updating account:', error);
      showToast('حدث خطأ في تحديث الحساب', 'error');
    }
  };

  const getStatusText = (status) => {
    // Use helper function for consistent status reading
    if (typeof status === 'object' && status !== null) {
      return getAccountStatus(status) ? 'مفعل' : 'معطل';
    }
    if (status === 'active' || status === true) return 'مفعل';
    return 'معطل';
  };

  const getStatusColor = (status) => {
    // Use helper function for consistent status reading
    let isActive = false;
    if (typeof status === 'object' && status !== null) {
      isActive = getAccountStatus(status);
    } else {
      isActive = status === 'active' || status === true;
    }

    if (isActive) {
      return { bg: 'rgba(63, 185, 132, 0.15)', color: '#3FB984' };
    }
    return { bg: 'rgba(242, 125, 114, 0.15)', color: '#F27D72' };
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#2B2F3F',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{
        flex: 1,
        padding: '32px',
        backgroundColor: '#2B2F3F',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        boxSizing: 'border-box'
      }}>
        {/* Page Title and Tabs Row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontFamily: 'El Messiri, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#FFFFFF',
            margin: 0,
            textAlign: 'right'
          }}>
            إدارة الحسابات
          </h2>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '12px'
          }}>
            {tabs.map((tab) => {
              let bgColor = '#F5F5F5';
              let textColor = '#666A7A';
              let border = '1px solid rgba(102, 106, 122, 0.22)';

              if (activeTab === tab.id) {
                if (tab.id === 'المستخدمين') {
                  bgColor = '#E96A3A';
                  textColor = '#FFFFFF';
                  border = 'none';
                } else if (tab.id === 'أصحاب المطابخ' || tab.id === 'أصحاب المتاجر') {
                  bgColor = '#F5F5F5';
                  textColor = '#666A7A';
                  border = '1px solid rgba(102, 106, 122, 0.22)';
                } else if (tab.id === 'الادمن') {
                  bgColor = '#F27D72';
                  textColor = '#FFFFFF';
                  border = 'none';
                }
              }

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setPage(1);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '12px',
                    border: border,
                    backgroundColor: bgColor,
                    color: textColor,
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    fontWeight: activeTab === tab.id ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    height: '42px',
                    boxSizing: 'border-box'
                  }}
                >
                  {tab.label}
                </button>
              );
            })}

            {/* Add Admin Button */}
            {activeTab === 'الادمن' && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAddAdminModal(true);
                }}
                disabled={addingAdmin}
                type="button"
                style={{
                  padding: '10px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  backgroundColor: addingAdmin ? '#cccccc' : '#E96A3A',
                  color: '#FFFFFF',
                  fontFamily: 'El Messiri, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: addingAdmin ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  height: '42px',
                  boxSizing: 'border-box',
                  opacity: addingAdmin ? 0.7 : 1
                }}
              >
                {addingAdmin ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid #FFFFFF',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></div>
                    جاري الإضافة...
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    إضافة أدمن
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Accounts Table */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '29px',
          padding: '24px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          flex: 1,
          overflow: 'auto',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              جاري التحميل...
            </div>
          ) : accounts.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              لا توجد حسابات
            </div>
          ) : (
            <>
              <div style={{ width: '100%', overflowX: 'auto', borderRadius: '29px' }}>
                <table style={{ minWidth: '1200px', width: '100%', borderCollapse: 'separate', borderSpacing: '0', direction: 'rtl', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '60px',
                        backgroundColor: '#2B2F3F',
                        borderRadius: '0 29px 0 0'
                      }}>#</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'center',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '70px',
                        backgroundColor: '#2B2F3F'
                      }}>الصورة</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        minWidth: '150px',
                        backgroundColor: '#2B2F3F'
                      }}>الاسم الكامل</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        minWidth: '130px',
                        backgroundColor: '#2B2F3F'
                      }}>رقم الهاتف</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        minWidth: '180px',
                        backgroundColor: '#2B2F3F'
                      }}>البريد الإلكتروني</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '80px',
                        backgroundColor: '#2B2F3F'
                      }}>الجنس</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        minWidth: '110px',
                        backgroundColor: '#2B2F3F'
                      }}>تاريخ الميلاد</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '100px',
                        backgroundColor: '#2B2F3F'
                      }}>المدينة</th>
                      {getCurrentUserType() === 'restaurant' && (
                        <>
                          <th style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'El Messiri, sans-serif',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            backgroundColor: '#2B2F3F'
                          }}>عدد الطلبات</th>
                          <th style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'El Messiri, sans-serif',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#FFFFFF',
                            backgroundColor: '#2B2F3F'
                          }}>التقييم</th>
                        </>
                      )}
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '100px',
                        whiteSpace: 'nowrap',
                        backgroundColor: '#2B2F3F'
                      }}>الحالة</th>
                      <th style={{
                        padding: '16px 12px',
                        textAlign: 'right',
                        fontFamily: 'El Messiri, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        width: '120px',
                        whiteSpace: 'nowrap',
                        backgroundColor: '#2B2F3F',
                        borderRadius: '29px 0 0 0'
                      }}>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map((account, index) => {
                      const statusInfo = getStatusColor(account);
                      return (
                        <tr
                          key={account.id}
                          onClick={() => handleViewDetails(account)}
                          style={{
                            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(233, 106, 58, 0.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '13px',
                            color: '#2B2F3F',
                            fontWeight: 600
                          }}>
                            <div style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px',
                              border: '1px solid rgba(43, 47, 63, 0.2)',
                              borderRadius: '3px',
                              backgroundColor: '#FFFFFF'
                            }}>
                              {(page - 1) * 10 + index + 1}
                            </div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{
                              width: '45px',
                              height: '45px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '2px solid rgba(233, 106, 58, 0.2)',
                              display: 'inline-block'
                            }}>
                              <img
                                src={account.profile_picture || account.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(account.fullName || account.name || 'User')}&background=E96A3A&color=fff`}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            </div>
                          </td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#2B2F3F',
                            fontWeight: 600,
                            minWidth: '150px'
                          }}>{account.fullName || account.name}</td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#2B2F3F',
                            direction: 'ltr'
                          }}>{account.phoneNumber || '-'}</td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#666A7A',
                            minWidth: '180px'
                          }}>{account.email || '-'}</td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#666A7A'
                          }}>{account.gender === 'male' ? 'ذكر' : account.gender === 'female' ? 'أنثى' : '-'}</td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#666A7A'
                          }}>{account.birthDate ? new Date(account.birthDate).toLocaleDateString('ar-EG') : '-'}</td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '14px',
                            color: '#666A7A'
                          }}>{account.city || '-'}</td>
                          {getCurrentUserType() === 'restaurant' && (
                            <>
                              <td style={{
                                padding: '16px 12px',
                                textAlign: 'right',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '14px',
                                color: '#666A7A'
                              }}>{account.ordersCount || 0}</td>
                              <td style={{
                                padding: '16px 12px',
                                textAlign: 'right',
                                fontFamily: 'Cairo, sans-serif',
                                fontSize: '14px',
                                color: '#666A7A'
                              }}>{account.averageRating ? account.averageRating.toFixed(1) : '0.0'}</td>
                            </>
                          )}
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right'
                          }}>
                            <span style={{
                              padding: '6px 14px',
                              borderRadius: '8px',
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '12px',
                              backgroundColor: statusInfo.bg,
                              color: statusInfo.color,
                              fontWeight: 600,
                              display: 'inline-block'
                            }}>
                              {getStatusText(account)}
                            </span>
                          </td>
                          <td style={{
                            padding: '16px 12px',
                            textAlign: 'right'
                          }}>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start', direction: 'rtl', alignItems: 'center' }}>
                              <button
                                onClick={(e) => handleEdit(account, e)}
                                style={{
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  color: '#2B2F3F',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px'
                                }}
                                title="تعديل"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(account.id);
                                }}
                                style={{
                                  padding: '8px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  color: '#F27D72',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px'
                                }}
                                title="حذف"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '24px',
                  paddingTop: '24px',
                  borderTop: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      backgroundColor: page === 1 ? 'rgba(176, 179, 187, 0.13)' : '#FFFFFF',
                      color: page === 1 ? '#666A7A' : '#2B2F3F',
                      cursor: page === 1 ? 'not-allowed' : 'pointer',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    السابق
                  </button>
                  <span style={{
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    color: '#666A7A',
                    padding: '0 16px'
                  }}>
                    صفحة {page} من {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      backgroundColor: page === totalPages ? 'rgba(176, 179, 187, 0.13)' : '#FFFFFF',
                      color: page === totalPages ? '#666A7A' : '#2B2F3F',
                      cursor: page === totalPages ? 'not-allowed' : 'pointer',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    التالي
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingAccount && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(43, 47, 63, 0.63)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '29px',
            padding: '48px',
            width: '499px',
            maxWidth: '90vw',
            border: '1px solid rgba(233, 106, 58, 0.12)',
            boxShadow: '0px 4px 23px 0px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box',
            direction: 'rtl'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '24px',
              textAlign: 'right',
              margin: '0 0 24px 0'
            }}>
              تعديل الحساب
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <input
                type="text"
                placeholder="الاسم"
                value={editingAccount.fullName || editingAccount.name || ''}
                onChange={(e) => setEditingAccount({
                  ...editingAccount,
                  fullName: e.target.value,
                  name: e.target.value
                })}
                style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(102, 106, 122, 0.22)',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                dir="rtl"
              />
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAccount(null);
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    backgroundColor: 'transparent',
                    color: '#666A7A',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSaveEdit}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#E96A3A',
                    color: '#FFFFFF',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  حفظ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(43, 47, 63, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease-out'
        }}
          onClick={() => !addingAdmin && setShowAddAdminModal(false)}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            padding: '40px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
            animation: 'slideUp 0.3s ease-out'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '24px',
              textAlign: 'right',
              margin: '0 0 24px 0'
            }}>
              إضافة أدمن جديد
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  الاسم الأول
                </label>
                <input
                  type="text"
                  value={newAdmin.firstName}
                  onChange={(e) => setNewAdmin({ ...newAdmin, firstName: e.target.value })}
                  placeholder="الاسم الأول"
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="rtl"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  اسم العائلة
                </label>
                <input
                  type="text"
                  value={newAdmin.lastName}
                  onChange={(e) => setNewAdmin({ ...newAdmin, lastName: e.target.value })}
                  placeholder="اسم العائلة"
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="rtl"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  البريد الإلكتروني
                </label>
                <input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="example@email.com"
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="ltr"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  كلمة المرور
                </label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder="••••••••"
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="ltr"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  تأكيد كلمة المرور
                </label>
                <input
                  type="password"
                  value={newAdmin.confirmPassword}
                  onChange={(e) => setNewAdmin({ ...newAdmin, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="ltr"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  الجنس
                </label>
                <select
                  value={newAdmin.gender}
                  onChange={(e) => setNewAdmin({ ...newAdmin, gender: e.target.value })}
                  disabled={addingAdmin}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: addingAdmin ? '#f5f5f5' : '#FFFFFF',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                >
                  <option value="male">ذكر</option>
                  <option value="female">أنثى</option>
                </select>
              </div>

              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => {
                    setShowAddAdminModal(false);
                    setNewAdmin({
                      firstName: '',
                      lastName: '',
                      email: '',
                      password: '',
                      confirmPassword: '',
                      gender: 'male',
                      birthDate: '1990-01-01'
                    });
                  }}
                  disabled={addingAdmin}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    backgroundColor: addingAdmin ? '#f5f5f5' : 'transparent',
                    color: addingAdmin ? '#999' : '#666A7A',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    cursor: addingAdmin ? 'not-allowed' : 'pointer',
                    fontWeight: 500
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={async () => {
                    if (!newAdmin.firstName || !newAdmin.lastName || !newAdmin.email || !newAdmin.password || !newAdmin.confirmPassword) {
                      showToast('يرجى ملء جميع الحقول', 'error');
                      return;
                    }

                    if (newAdmin.password !== newAdmin.confirmPassword) {
                      showToast('كلمة المرور وتأكيدها غير متطابقين', 'error');
                      return;
                    }

                    if (newAdmin.password.length < 6) {
                      showToast('كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'error');
                      return;
                    }

                    // Validate password strength
                    const passwordRegex = /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/;
                    if (!passwordRegex.test(newAdmin.password)) {
                      showToast('كلمة المرور يجب أن تحتوي على حرف كبير وصغير ورقم أو رمز خاص', 'error');
                      return;
                    }

                    setAddingAdmin(true);
                    try {
                      const response = await api.register({
                        firstName: newAdmin.firstName.trim(),
                        lastName: newAdmin.lastName.trim(),
                        email: newAdmin.email.trim(),
                        password: newAdmin.password,
                        confirmPassword: newAdmin.confirmPassword,
                        gender: newAdmin.gender,
                        birthDate: newAdmin.birthDate,
                        userType: 'admin'
                      });

                      console.log('Register response:', response);

                      // Check if registration was successful
                      if (response && (response.user || response.id)) {
                        showToast('تم إضافة الأدمن بنجاح', 'success');
                        setShowAddAdminModal(false);
                        setNewAdmin({
                          firstName: '',
                          lastName: '',
                          email: '',
                          password: '',
                          confirmPassword: '',
                          gender: 'male',
                          birthDate: '1990-01-01'
                        });
                        // Refresh the accounts list
                        await fetchAccounts();
                      } else {
                        throw new Error('لم يتم إنشاء الأدمن بشكل صحيح');
                      }
                    } catch (error) {
                      console.error('Error adding admin:', error);
                      // Extract error message from response if available
                      let errorMessage = 'حدث خطأ في إضافة الأدمن';
                      if (error.message) {
                        errorMessage = error.message;
                      } else if (error.data && error.data.message) {
                        errorMessage = Array.isArray(error.data.message)
                          ? error.data.message.join(', ')
                          : error.data.message;
                      }
                      showToast(errorMessage, 'error');
                    } finally {
                      setAddingAdmin(false);
                    }
                  }}
                  disabled={addingAdmin}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: addingAdmin ? '#cccccc' : '#E96A3A',
                    color: '#FFFFFF',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    cursor: addingAdmin ? 'not-allowed' : 'pointer',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {addingAdmin ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #FFFFFF',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}></div>
                      جاري الإضافة...
                    </>
                  ) : (
                    'إضافة'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Account Details Modal */}
      {showDetailsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(43, 47, 63, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          overflow: 'auto',
          direction: 'rtl'
        }}
          onClick={() => setShowDetailsModal(false)}
        >
          <div style={{
            width: '100%',
            maxWidth: '1000px',
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            padding: '32px',
            boxSizing: 'border-box',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#2B2F3F',
                margin: 0
              }}>
                تفاصيل الحساب
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: 'rgba(102, 106, 122, 0.1)',
                  color: '#666A7A',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(242, 125, 114, 0.2)';
                  e.currentTarget.style.color = '#F27D72';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(102, 106, 122, 0.1)';
                  e.currentTarget.style.color = '#666A7A';
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {loadingDetails ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '60px',
                color: '#666A7A',
                fontFamily: 'Cairo, sans-serif'
              }}>
                جاري التحميل...
              </div>
            ) : accountDetails || selectedAccount ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px'
              }}>
                {/* Right Card - User Details */}
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '32px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  position: 'relative'
                }}>
                  {/* Profile Picture */}
                  <div style={{
                    position: 'absolute',
                    left: '32px',
                    top: '32px',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #FFFFFF',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    <img
                      src={accountDetails?.profile_picture || accountDetails?.logo_url || selectedAccount?.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(accountDetails?.name || accountDetails?.fullName || selectedAccount?.name || selectedAccount?.fullName || 'User')}&background=E96A3A&color=fff&size=160`}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </div>

                  {/* User Info */}
                  <div style={{ marginTop: '20px' }}>
                    <label style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#666A7A',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      الاسم الكامل
                    </label>
                    <p style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#2B2F3F',
                      margin: 0
                    }}>
                      {accountDetails?.name || accountDetails?.fullName || `${accountDetails?.firstName || ''} ${accountDetails?.lastName || ''}`.trim() || selectedAccount?.name || selectedAccount?.fullName || '-'}
                    </p>
                  </div>

                  <div>
                    <label style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#666A7A',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      نوع الحساب
                    </label>
                    <p style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#2B2F3F',
                      margin: 0
                    }}>
                      {getCurrentUserType() === 'normalUser' ? 'مستخدم' :
                        getCurrentUserType() === 'admin' ? 'أدمن' :
                          getCurrentUserType() === 'restaurant' ? 'مطبخ' : 'متجر'}
                    </p>
                  </div>

                  <div>
                    <label style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#666A7A',
                      display: 'block',
                      marginBottom: '8px'
                    }}>
                      المدينة
                    </label>
                    <p style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#2B2F3F',
                      margin: 0
                    }}>
                      {accountDetails?.city || accountDetails?.country?.name || selectedAccount?.city || '-'}
                    </p>
                  </div>

                  {(accountDetails?.email || accountDetails?.owner?.email || selectedAccount?.email) && (
                    <div>
                      <label style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#666A7A',
                        display: 'block',
                        marginBottom: '8px'
                      }}>
                        البريد الإلكتروني
                      </label>
                      <p style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '14px',
                        color: '#2B2F3F',
                        margin: 0
                      }}>
                        {accountDetails?.email || accountDetails?.owner?.email || selectedAccount?.email || '-'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Left Card - Status and Actions */}
                <div style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '28px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  {/* Status Display/Toggle */}
                  <div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <label style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F'
                      }}>
                        الحالة
                      </label>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        {/* Toggle Button for Restaurants/Stores */}
                        {(getCurrentUserType() === 'restaurant' || getCurrentUserType() === 'store') ? (
                          <>
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const accountId = selectedAccount?.id || accountDetails?.id;
                                if (!accountId) {
                                  console.error('No account ID found');
                                  showToast('خطأ: لم يتم العثور على معرف الحساب', 'error');
                                  return;
                                }
                                // Use helper function for consistent status reading
                                const currentStatus = getAccountStatus(accountDetails);
                                const newStatus = !currentStatus;
                                console.log('🔄 Toggling status (button click):', {
                                  accountId,
                                  currentStatus,
                                  newStatus,
                                  newStatusType: typeof newStatus,
                                  accountDetailsIsActive: accountDetails?.isActive,
                                  accountDetailsStatus: accountDetails?.status,
                                  accountDetailsActive: accountDetails?.active,
                                  accountDetails
                                });

                                // Call handleToggleStatus and wait for it to complete
                                // handleToggleStatus will update accountDetails internally
                                await handleToggleStatus(accountId, newStatus);

                                // No need for setTimeout - handleToggleStatus already updates accountDetails
                                // The UI will re-render automatically when accountDetails state changes
                              }}
                              style={{
                                width: '48px',
                                height: '26px',
                                borderRadius: '13px',
                                border: 'none',
                                backgroundColor: getAccountStatus(accountDetails) ? '#3FB984' : 'rgba(176, 179, 187, 0.3)',
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'all 0.3s',
                                padding: 0,
                                outline: 'none'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '0.8';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '1';
                              }}
                            >
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                position: 'absolute',
                                top: '3px',
                                right: getAccountStatus(accountDetails) ? '3px' : '25px',
                                transition: 'all 0.3s',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                              }} />
                            </button>
                            <span style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              fontFamily: 'Cairo, sans-serif',
                              fontSize: '11px',
                              backgroundColor: getAccountStatus(accountDetails) ? 'rgba(63, 185, 132, 0.15)' : 'rgba(242, 125, 114, 0.15)',
                              color: getAccountStatus(accountDetails) ? '#3FB984' : '#F27D72',
                              fontWeight: 600
                            }}>
                              {getAccountStatus(accountDetails) ? 'مفعل' : 'معطل'}
                            </span>
                          </>
                        ) : (
                          /* Read-only Status Badge for Users/Admins */
                          <span style={{
                            padding: '5px 12px',
                            borderRadius: '20px',
                            fontFamily: 'Cairo, sans-serif',
                            fontSize: '11px',
                            backgroundColor: 'rgba(63, 185, 132, 0.15)',
                            color: '#3FB984',
                            fontWeight: 600,
                            display: 'inline-block'
                          }}>
                            نشط
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => {
                      if (window.confirm('هل أنت متأكد من حذف هذا الحساب؟')) {
                        setShowDetailsModal(false);
                        handleDelete(selectedAccount?.id || accountDetails?.id);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: '#F27D72',
                      color: '#FFFFFF',
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      marginTop: 'auto',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e66d63';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#F27D72';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    حذف الحساب
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '60px',
                color: '#666A7A',
                fontFamily: 'Cairo, sans-serif'
              }}>
                لا توجد تفاصيل متاحة
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default إدارةالحسابات;
